import React from 'react';
import {
    PiCheckCircleDuotone,
    PiInfoDuotone,
    PiWarningCircleDuotone,
    PiXCircleDuotone,
} from 'react-icons/pi';

import Modal from 'components/Modal';

const FEEDBACK_TONES = {
    success: {
        Icon: PiCheckCircleDuotone,
        eyebrow: 'Sikeres művelet',
        iconClassName: 'bg-lime-300/20 text-lime-300 ring-lime-200/20',
    },
    error: {
        Icon: PiXCircleDuotone,
        eyebrow: 'Hiba történt',
        iconClassName: 'bg-red-300/20 text-red-200 ring-red-200/20',
    },
    warning: {
        Icon: PiWarningCircleDuotone,
        eyebrow: 'Figyelem',
        iconClassName: 'bg-amber-300/20 text-amber-200 ring-amber-200/20',
    },
    info: {
        Icon: PiInfoDuotone,
        eyebrow: 'Információ',
        iconClassName: 'bg-sky-300/20 text-sky-200 ring-sky-200/20',
    },
};

export function FeedbackContent({
    tone = 'success',
    title,
    message,
    children,
    actions,
}) {
    const config = FEEDBACK_TONES[tone] || FEEDBACK_TONES.info;
    const { Icon } = config;

    return (
        <div className="relative z-10 flex min-w-0 flex-col text-white">
            <div className="flex min-w-0 items-center gap-3 px-5 pb-4 pt-5 sm:gap-4 sm:px-6 sm:pt-6">
                <span className={`flex size-12 shrink-0 items-center justify-center rounded-2xl ring-1 sm:size-14 ${config.iconClassName}`}>
                    <Icon className="size-7 sm:size-8" />
                </span>

                <div className="min-w-0">
                    <p className="mb-0.5 text-xs font-black uppercase tracking-[0.14em] text-white/65">
                        {config.eyebrow}
                    </p>
                    <h2 className="break-words font-display text-2xl font-black leading-tight sm:text-3xl">
                        {title}
                    </h2>
                </div>
            </div>

            <div className="mx-2 min-w-0 rounded-2xl bg-white p-5 text-neutral-900 shadow-2xl sm:p-6">
                {message && (
                    <p className="break-words font-semibold leading-relaxed text-neutral-700">
                        {message}
                    </p>
                )}
                {children}
            </div>

            {actions && (
                <div className="flex flex-col gap-3 p-4 sm:flex-row">
                    {actions}
                </div>
            )}
        </div>
    );
}

export function FeedbackPanel({
    tone = 'success',
    title,
    message,
    children,
    actions,
    className = '',
}) {
    return (
        <div className={`relative min-w-0 w-[calc(100vw-2rem)] max-w-[520px] overflow-hidden rounded-3xl gradient-gray text-white shadow-2xl ring-8 ring-white/20 ${className}`}>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/25 via-white/5 to-transparent" />
            <div className="pointer-events-none absolute -left-24 -top-24 size-64 rounded-full bg-white/10 blur-3xl" />
            <FeedbackContent
                tone={tone}
                title={title}
                message={message}
                actions={actions}
            >
                {children}
            </FeedbackContent>
        </div>
    );
}

export default function FeedbackModal({
    open,
    onClose,
    tone = 'success',
    title,
    message,
    children,
    actionLabel = 'Rendben',
    onAction,
    secondaryActionLabel,
    onSecondaryAction,
    showClose = true,
}) {
    const actions = (
        <>
            {secondaryActionLabel && (
                <button
                    type="button"
                    onClick={onSecondaryAction || onClose}
                    className="form-action-button form-action-secondary flex-1"
                >
                    {secondaryActionLabel}
                </button>
            )}
            <button
                type="button"
                onClick={onAction || onClose}
                className="form-action-button form-action-primary flex-1"
            >
                {actionLabel}
            </button>
        </>
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            maxWidth="max-w-[500px]"
            className="!w-full"
            showClose={showClose}
        >
            <FeedbackContent
                tone={tone}
                title={title}
                message={message}
                actions={actions}
            >
                {children}
            </FeedbackContent>
        </Modal>
    );
}
